import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useQuery } from "@tanstack/react-query";

import { createChannelUrl, MESSAGE_REFRESH_INTERVAL } from "../constants";

interface UseChatChannelParams {
  workplaceId: string;
  workerId: string;
  /** When provided, fetches the placement-specific channel (3-part URL). */
  placementId?: string;
  enabled?: boolean;
}

/**
 * Hook to get an existing Sendbird GroupChannel by workplaceId and workerId.
 * Returns null if the channel doesn't exist yet.
 *
 * @example
 * ```tsx
 * const { data: channel, isLoading } = useChatChannel({
 *   workplaceId: "workplace123",
 *   workerId: "worker456",
 * });
 * ```
 */
export function useChatChannel({
  workplaceId,
  workerId,
  placementId,
  enabled = true,
}: UseChatChannelParams) {
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;

  return useQuery({
    queryKey: ["chat", "channel", workplaceId, workerId, placementId],
    queryFn: async (): Promise<GroupChannel | null> => {
      if (!sdk) {
        throw new Error("Sendbird SDK not initialized");
      }

      const baseUrl = createChannelUrl(workplaceId, workerId);
      const channelUrl = placementId ? `${baseUrl}_${placementId}` : baseUrl;

      try {
        const channel = await sdk.groupChannel.getChannel(channelUrl);
        return channel;
      } catch (error) {
        // Channel doesn't exist yet - return null
        if (error && typeof error === "object" && "code" in error && error.code === 400_111) {
          return null;
        }
        throw error;
      }
    },
    enabled: enabled && Boolean(sdk) && Boolean(workplaceId) && Boolean(workerId),
    staleTime: MESSAGE_REFRESH_INTERVAL,
    retry: (failureCount, error) => {
      // Don't retry if channel doesn't exist (404/400111)
      if (error && typeof error === "object" && "code" in error && error.code === 400_111) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
